import { z } from 'zod';

/**
 * How many decimal places money carries on this platform.
 *
 * ## One convention, and this is it: MAJOR units
 *
 * A price of twenty-five riyals is `25`, never `2500`. Every money value in the
 * tree already worked this way — discount amounts, `DiscountOutcome.finalPrice`,
 * `discount_redemptions.amountOff`, the member app's `toFixed(2)` label, the
 * admin's `MoneyInput` — while `billing.directPrices` alone *said* "smallest
 * currency unit" in its docblock and typed itself `int`. Nothing had ever
 * written a value there, so P23a1 corrected the outlier rather than converting
 * at every boundary.
 *
 * Why it matters concretely: `CoursesGetActiveDiscountQuery` takes a `price` and
 * hands it to `resolveDiscountOutcome`. Feed that minor units and a **percent**
 * grant still looks right while a **fixed** one is silently off by a factor of a
 * hundred — a 25 SAR discount taking 25 *halalas* off a 9900 price. That defect
 * would have surfaced as an operator's complaint about a wrong invoice, months
 * later, with no failing test anywhere.
 *
 * ## Known limit, recorded rather than papered over
 *
 * Four currencies already in `BILLING_CURRENCIES` — KWD, BHD, OMR, JOD — carry
 * **three** decimal places, so this constant under-represents them: a price of
 * 12.345 KWD cannot be expressed. Making the precision currency-dependent
 * changes shipped courses arithmetic, its specs, the admin money control and the
 * mobile formatter, so it is a phase of its own rather than a side effect of
 * this one. See PROGRESS §Known issues.
 */
export const MONEY_DECIMALS = 2;

const MONEY_STEP = 10 ** MONEY_DECIMALS;

/**
 * Rounds to the platform's money precision.
 *
 * Rounding at every step rather than only at the end is deliberate: a price is a
 * number an operator typed and a member is charged, so an intermediate carrying
 * float noise (`0.1 + 0.2`) must not reach a comparison. Agrees by construction
 * with the rounding inside `resolveDiscountOutcome`, which is the only other
 * place money arithmetic happens.
 */
export function roundMoney(value: number): number {
  return Math.round(value * MONEY_STEP) / MONEY_STEP;
}

/**
 * Whether a number is expressible as money here.
 *
 * Checked with integer arithmetic rather than a regex or a `toFixed` comparison,
 * so `1.1` — which has no exact binary representation — is accepted while
 * `1.005` is not. The extra `* 1000` absorbs the representation error before the
 * integer test, which is the same guard `DiscountAmountSchema` uses.
 */
export function isMoneyAmount(value: number): boolean {
  return Number.isFinite(value) && Number.isInteger(Math.round(value * MONEY_STEP * 1000) / 1000);
}

/**
 * A price, in major units.
 *
 * **Non-negative rather than positive**: zero is a legitimate price — a free
 * tier, or a plan a deployment has made complimentary — and refusing it would
 * force an operator to delete the product instead of pricing it at nothing.
 * A *negative* price would be a payout issued through the pricing pipeline.
 */
export const MoneyAmountSchema = z
  .number()
  .nonnegative()
  .refine(isMoneyAmount, { message: `Must have at most ${MONEY_DECIMALS} decimal places.` });
